<template>
    <div class="email-rule">
        <custom-search-resource-select
            :custom-filter="filterEmails"
            :value="emailId"
            :options="emails"
            :loading="loadingResources"
            field="emailId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('emailRule.Select Email')"
            @input="onEmailSelect">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </custom-search-resource-select>
        <template v-if="emailId">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="timeOptions"
                :value="timeValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="time"
                @input="$emit('input', $event)" />
            <component
                :is="valueComponent.component"
                v-if="valueComponent"
                v-bind="valueComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import { fitEmailStatus } from '@/emails/helpers';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import CustomSearchResourceSelect from '@/segments/components/segment-editor-vue/components/custom-search-resource-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    getTimeOptions,
    formatFactOptions,
    getAbsoluteDateRangeConfig,
    getFactOptions,
    getFactValue,
    getNoResourcesFoundText,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'EmailRule',
    components: {
        FactSelect,
        CustomSearchResourceSelect,
        SimpleSelect,
        SingleDate,
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        emails () {
            if (this.loadingResources) return [];

            return [
                {
                    label: 'default',
                    options: [
                        {
                            name: this.$t('emailRule.Any Email'),
                            tooltip: { content: this.$t('emailRule.Any Email') },
                            emailId: '*',
                        },
                    ],
                },
                {
                    label: 'divider',
                    options: this.resources.map(({ id: emailId, name, status }) => {
                        return {
                            emailId,
                            name,
                            tooltip: { content: name },
                            state: fitEmailStatus(status),
                        };
                    }),
                },
            ];
        },
        emailId () {
            return this.emails
                .reduce((acc, group) => acc.concat(group.options), [])
                .find(({ emailId }) => emailId === this.rule.emailId);
        },
        factOptions () {
            const options = getFactOptions(this.rule);

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        timeOptions () {
            return [...getTimeOptions().email];
        },
        valueComponent () {
            switch (this.rule.time) {
                case 'withinLast':
                case '!withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case '>=':
                case '<=':
                    return getSingleDateConfig(this.rule, 'first');
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
    methods: {
        onEmailSelect (event) {
            const field = ['emailId'];
            const value = { emailId: event.value.emailId };

            this.$emit('input', { field, value });
        },
        filterEmails ({ query, options }) {
            const searchTerm = query?.toLowerCase().trim() || '';

            if (!searchTerm || !Array.isArray(options)) return options;

            const [anyGroup, emailGroup] = options;

            if (!emailGroup?.options) return options;

            return [
                anyGroup,
                {
                    ...emailGroup,
                    options: emailGroup.options.filter((option) => option?.name?.toLowerCase().includes(searchTerm)),
                },
            ];
        },
    },
};
</script>

<style scoped lang="less">
:deep(.pendo-picker-popper__content:has(.pendo-multiselect__no-results)) {
    width: fit-content;
}
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
